import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import PageHeader from "../../../../components/common/PageHeader";
import PageLoader from "../../../../components/common/PageLoader";
import FormField from "../../../../components/common/FormField";
import PasswordField from "../../../../components/common/PasswordField";
import ChipAutocomplete from "../../../../components/common/ChipAutocomplete";
import useFormValidation from "../../../../hooks/useFormValidation";
import {
  email,
  matchField,
  minLength,
  mobile,
  required,
  sanitizeNationalPhone,
  toApiMobileNumber,
} from "../../../../utils/formValidation";
import { USER_TYPES } from "../../constants";
import { systemUserService } from "../../services/systemUser.service";
import { tenantUserService } from "../../services/tenantUser.service";
import { roleService } from "../../services/role.service";
import {
  catalogLabels,
  filterByCatalog,
} from "../../../../helpers/catalogFilter.helper";
import { tenantRoleService } from "../../../role-management/services/tenantRole.service";
import { normalizeRoleList } from "../../../role-management/utils/normalizeRole";
import { normalizeUser } from "../../utils/normalizeUser";
import { appActions } from "../../../../redux/actions";
import { getApiErrorMessage } from "../../../../helpers";
import useAuth from "../../../../hooks/useAuth";

/** Password optional on edit — validate only when user types a new value. */
const optionalPassword =
  (message = "Password must be at least 4 characters.") =>
  (value) => {
    if (!String(value || "").trim()) return "";
    return minLength(4, message)(value);
  };

const optionalConfirmPassword =
  () =>
  (value, values) => {
    if (!String(values.password || "").trim() && !String(value || "").trim()) {
      return "";
    }
    if (!String(value || "").trim()) {
      return "Confirm password is required when changing password.";
    }
    return matchField("password", "Passwords do not match.")(value, values);
  };

const systemSchema = {
  firstname: [required("First name is required.")],
  lastname: [],
  username: [required("Username is required.")],
  email: [required("Email is required."), email()],
  mobile: [required("Mobile is required."), mobile()],
  password: [optionalPassword()],
  confirmPassword: [optionalConfirmPassword()],
};

const tenantSchema = {
  firstname: [required("First name is required.")],
  lastname: [],
  username: [required("Username is required.")],
  email: [required("Email is required."), email()],
  mobile: [required("Mobile is required."), mobile()],
  password: [optionalPassword()],
  confirmPassword: [optionalConfirmPassword()],
};

const UserEditPage = ({ userType = USER_TYPES.TENANT }) => {
  const isSystem = userType === USER_TYPES.SYSTEM;
  const { userId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { activeTenantId, tenantId: authTenantId } = useAuth();
  const tenantId = activeTenantId || authTenantId;
  const listPath = isSystem ? "/systemusers" : "/tenantusers";
  const detailsPath = `${listPath}/view/${encodeURIComponent(userId || "")}`;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [initialRoles, setInitialRoles] = useState([]);
  const [initialProfile, setInitialProfile] = useState(null);

  const schema = isSystem ? systemSchema : tenantSchema;
  const { values, getFieldProps, validateAll, reset, setFieldValue, isValid } =
    useFormValidation(
      {
        firstname: "",
        lastname: "",
        username: "",
        email: "",
        mobile: "",
        password: "",
        confirmPassword: "",
      },
      schema
    );

  useEffect(() => {
    let cancelled = false;
    const loadRoles = async () => {
      setRolesLoading(true);
      try {
        if (isSystem) {
          const list = await roleService.getAllRoles();
          if (!cancelled) setRoles(list || []);
        } else {
          const payload = await tenantRoleService.getAllTenantRoles(tenantId);
          if (!cancelled) {
            setRoles(
              normalizeRoleList(payload)
                .map((role) => role.name)
                .filter(Boolean)
            );
          }
        }
      } catch {
        if (!cancelled) setRoles([]);
      } finally {
        if (!cancelled) setRolesLoading(false);
      }
    };
    loadRoles();
    return () => {
      cancelled = true;
    };
  }, [isSystem, tenantId]);

  const loadUser = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setFormError("");
    try {
      const detail = isSystem
        ? await systemUserService.getSystemUserById(userId)
        : await tenantUserService.getTenantUserById(userId, tenantId);
      const user = normalizeUser(detail || {});
      const userRoles = Array.isArray(user.roles)
        ? user.roles.map(String).filter(Boolean)
        : [];
      setSelectedRoles(userRoles);
      setInitialRoles(userRoles);
      setEnabled(user.enabled !== false);

      const mobileNational = sanitizeNationalPhone(user.mobile || "");
      const profile = {
        firstname: user.firstname || "",
        lastname: user.lastname || "",
        username: user.username || "",
        email: user.email || "",
        mobile: mobileNational,
        ...(isSystem ? { enabled: user.enabled !== false } : {}),
      };
      setInitialProfile(profile);
      reset({
        firstname: profile.firstname,
        lastname: profile.lastname,
        username: profile.username,
        email: profile.email,
        mobile: profile.mobile,
        password: "",
        confirmPassword: "",
      });
    } catch (err) {
      setFormError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [isSystem, reset, tenantId, userId]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const roleOptions = useMemo(() => catalogLabels(roles), [roles]);

  useEffect(() => {
    if (rolesLoading) return;
    setSelectedRoles((prev) => filterByCatalog(prev, roles));
  }, [roles, rolesLoading]);

  const passwordFilled = Boolean(String(values.password || "").trim());
  const canSubmit = Boolean(
    !saving &&
      selectedRoles.length &&
      isValid &&
      (!passwordFilled ||
        (values.confirmPassword && values.password === values.confirmPassword))
  );

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    const { isValid: ok, errors } = validateAll();
    if (!ok) {
      setFormError(
        Object.values(errors).find(Boolean) ||
          "Please correct the highlighted fields."
      );
      return;
    }
    if (!selectedRoles.length) {
      setFormError("Select at least one role.");
      return;
    }

    const nextPassword = String(values.password || "").trim();
    const rolesChanged =
      selectedRoles.length !== initialRoles.length ||
      selectedRoles.some((role) => !initialRoles.includes(role)) ||
      initialRoles.some((role) => !selectedRoles.includes(role));

    const currentMobile = sanitizeNationalPhone(values.mobile);
    const baseline = initialProfile || {};

    const profileChanged =
      values.firstname.trim() !== String(baseline.firstname || "") ||
      String(values.lastname || "").trim() !==
        String(baseline.lastname || "") ||
      values.email.trim() !== String(baseline.email || "") ||
      currentMobile !== String(baseline.mobile || "") ||
      (isSystem &&
        (values.username.trim() !== String(baseline.username || "") ||
          enabled !== Boolean(baseline.enabled)));

    if (!profileChanged && !nextPassword && !rolesChanged) {
      setFormError("No changes to save.");
      return;
    }

    setFormError("");
    setSaving(true);

    try {
      if (isSystem) {
        if (profileChanged) {
          const firstname = values.firstname.trim();
          const username = values.username.trim();
          const emailValue = values.email.trim();
          const mobileChanged =
            currentMobile !== String(baseline.mobile || "");
          await systemUserService.updateSystemUser(userId, {
            firstname,
            lastname: String(values.lastname || "").trim(),
            username,
            email: emailValue,
            // Do not resend unchanged mobile (uniqueness errors).
            mobile: mobileChanged
              ? toApiMobileNumber(currentMobile)
              : toApiMobileNumber(baseline.mobile || currentMobile),
            enabled,
          });
        }
        if (nextPassword) {
          await systemUserService.changeSystemUserPassword(userId, nextPassword);
        }
        if (rolesChanged) {
          await systemUserService.updateSystemUserRole(userId, selectedRoles);
        }
      } else {
        if (profileChanged) {
          const profilePayload = {};
          const firstname = values.firstname.trim();
          const lastname = String(values.lastname || "").trim();
          const emailValue = values.email.trim();
          if (
            firstname !== String(baseline.firstname || "") ||
            lastname !== String(baseline.lastname || "")
          ) {
            profilePayload.firstname = firstname;
            profilePayload.lastname = lastname;
          }
          if (emailValue !== String(baseline.email || "")) {
            profilePayload.email = emailValue;
          }
          // Send mobile only when it changed.
          if (currentMobile !== String(baseline.mobile || "")) {
            profilePayload.mobile = toApiMobileNumber(currentMobile);
          }
          if (Object.keys(profilePayload).length) {
            await tenantUserService.updateTenantUser(
              userId,
              profilePayload,
              tenantId
            );
          }
        }
        if (nextPassword) {
          await tenantUserService.changeTenantUserPassword(
            userId,
            nextPassword,
            tenantId
          );
        }
        if (rolesChanged) {
          await tenantUserService.updateTenantUserRole(
            userId,
            selectedRoles,
            tenantId
          );
        }
      }

      dispatch(appActions.apiSuccessMessage("User updated successfully."));
      navigate(detailsPath);
    } catch (err) {
      setFormError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader text="Loading user..." />;
  }

  return (
    <Box sx={{ width: "100%", alignSelf: "stretch" }}>
      <Button
        startIcon={<ArrowBackOutlinedIcon />}
        onClick={() => navigate(detailsPath)}
        sx={{ mb: 0.75, px: 0.5 }}
      >
        Back to details
      </Button>
      <PageHeader
        title="Edit User"
        subtitle="Update account details and roles."
        sx={{ mb: 2 }}
      />

      <Paper
        elevation={0}
        component="form"
        noValidate
        onSubmit={handleSubmit}
        sx={{
          width: "100%",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2.5,
          overflow: "hidden",
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        }}
      >
        <Box sx={{ p: { xs: 2.5, md: 3 } }}>
          {formError ? (
            <Alert
              severity="error"
              sx={{ mb: 2.5 }}
              onClose={() => setFormError("")}
            >
              {formError}
            </Alert>
          ) : null}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr",
                lg: "1fr 1fr 1fr",
              },
              gap: 2.25,
              alignItems: "start",
            }}
          >
            <FormField
              label="First Name"
              required
              {...getFieldProps("firstname")}
            />
            <FormField label="Last Name" {...getFieldProps("lastname")} />
            <FormField
              label="Username"
              required
              {...getFieldProps("username")}
              disabled
              helperText="Username cannot be changed."
            />
            <FormField
              label="Email"
              required
              type="email"
              {...getFieldProps("email")}
            />
            <FormField
              label="Mobile"
              required
              {...getFieldProps("mobile")}
              onChange={(event) =>
                setFieldValue(
                  "mobile",
                  sanitizeNationalPhone(event.target.value)
                )
              }
              placeholder="10-digit mobile"
              inputProps={{ inputMode: "numeric", maxLength: 10 }}
            />
            <PasswordField
              label="Password"
              {...getFieldProps("password")}
              helperText="Optional. Leave blank to keep the current password."
            />
            <PasswordField
              label="Confirm Password"
              {...getFieldProps("confirmPassword")}
              helperText="Required only when setting a new password."
            />
            <ChipAutocomplete
              label="Roles"
              required
              freeSolo={!isSystem}
              options={roleOptions}
              value={selectedRoles}
              onChange={setSelectedRoles}
              loading={rolesLoading}
              disabled={saving}
              helperText={
                selectedRoles.length
                  ? isSystem
                    ? "Only IDP system roles are listed."
                    : " "
                  : "Select at least one role."
              }
            />
          </Box>
        </Box>

        <Stack
          direction="row"
          spacing={1.5}
          justifyContent="flex-end"
          sx={{
            px: { xs: 2.5, md: 3 },
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "grey.50",
          }}
        >
          <Button
            onClick={() => navigate(detailsPath)}
            disabled={saving}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit}
            sx={{ minWidth: 132 }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default UserEditPage;
